import { groupBy } from "ramda";
import { type FC, useMemo, useState } from "react";

import type { GridCard } from "widgets";
import { Card, CardRowSimple, LoadingSpinner, TriggerButton } from "widgets";

import { useError } from "@/app-utils/graphql/useError.ts";
import { useNotificationSummaryViewUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { AsyncSearchWithExprParser } from "@/ui-modules/core/forms/AsyncSearchWithExprParser.tsx";
import { filterCategorizedResources } from "@/ui-modules/entities/cards/utils/card-utils.ts";
import type { CategorizedResources } from "@/ui-modules/playbooks/events/PlaybookEventResources.tsx";
import { PlaybookEventResources } from "@/ui-modules/playbooks/events/PlaybookEventResources.tsx";
import { useGetPlaybookEventOutputsCardQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

interface IPlaybookEventOutputCardProps extends GridCard {
  eventId: number;
}

export const PlaybookEventOutputCard: FC<IPlaybookEventOutputCardProps> = ({ eventId }) => {
  const { data, loading, error, refetch } = useGetPlaybookEventOutputsCardQuery({
    variables: { eventId: eventId },
  });
  void useError(error);
  useNotificationSummaryViewUpdate(refetch, eventId);

  const playbookEvent = data?.playbook?.event;

  const [isExpanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);

  const categorizedResources = useMemo(() => {
    if (!Array.isArray(playbookEvent?.results)) {
      return [];
    }

    // first key the resources by resourceType (for stacking logic)
    const categories = groupBy(
      (eventResource) => eventResource!.resourceType ?? "Unknown",
      playbookEvent.results ?? [],
    );

    // create objects using reusable interface
    return Object.keys(categories).map(
      (category) =>
        ({
          category,
          resources: categories[category],
        }) as CategorizedResources,
    );
  }, [playbookEvent?.results]);

  const filteredCategorizedResources = filterCategorizedResources(filter ?? "", categorizedResources);
  const isExpandable =
    filteredCategorizedResources.length > 1 &&
    filteredCategorizedResources.reduce(
      (previous, currentValue) => previous || (currentValue?.resources?.length ?? 0) > 1,
      false,
    );

  return (
    <Card
      key="event-output-card"
      title={"Output"}
      actions={[
        <AsyncSearchWithExprParser
          onSearchExprChange={async (value) => setFilter(value?.toLowerCase())}
          key={"playbook-event-card-output-search"}
        />,
        isExpandable && (
          <TriggerButton
            onPress={() => setExpanded((e) => !e)}
            icon={isExpanded ? "unfold_less_double" : "unfold_more_double"}
            label={isExpanded ? "Collapse Files of Same Type" : "Expand Stacked Files"}
            key={"playbook-event-card-output-expand-button"}
          />
        ),
      ]}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {!categorizedResources.length ? (
            <ContentPlaceholder>No Outputs</ContentPlaceholder>
          ) : (
            !filteredCategorizedResources.length && <ContentPlaceholder text={"No output matching your filter"} />
          )}
          <CardRowSimple>
            {filteredCategorizedResources.map((categorizedResources, i) => (
              <PlaybookEventResources
                categorizedResources={categorizedResources}
                single={filteredCategorizedResources.length === 1}
                isExpanded={isExpanded}
                key={`playbook-event-card-output${i}`}
              />
            ))}
          </CardRowSimple>
        </>
      )}
    </Card>
  );
};
